import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Link,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import MarketingInfoBar from "./MarketingInfoBar";
import PageMeta from "./PageMeta";

const CONTACT_METHODS = [
  {
    icon: EmailOutlinedIcon,
    label: "Sales & general",
    description: "Talk to our team about InfraBlaze for your org.",
    value: "contact@infrablaze.com",
    href: "mailto:contact@infrablaze.com",
  },
  {
    icon: LockOutlinedIcon,
    label: "Security & compliance",
    description: "Data handling, privacy, and enterprise controls.",
    value: "security@infrablaze.com",
    href: "mailto:security@infrablaze.com",
  },
  {
    icon: PlayCircleOutlineIcon,
    label: "Product walkthrough",
    description: "See inventory, Auto-IaC, and policy gates in action.",
    value: "Schedule a demo",
    href: "#demo",
  },
];

const TRUST_SIGNALS = [
  "Multi-cloud support (AWS, Azure, GCP)",
  "Self-hosted or SaaS deployment",
  "SSO, RBAC, and audit logging",
  "Responds within 1 business day",
];

export default function ContactUs() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [mounted, setMounted] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      setReducedMotion(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    }
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  const fadeUp = (delay = 0) => ({
    opacity: mounted ? 1 : 0,
    transform: mounted ? "translateY(0)" : "translateY(24px)",
    transition: `opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
  });

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    role: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (key) => (e) => {
    setForm((p) => ({ ...p, [key]: e.target.value }));
  };

  const isValid = useMemo(() => {
    const hasName = (form.firstName || "").trim().length > 0;
    const hasEmail = (form.email || "").trim().length > 3;
    const hasMessage = (form.message || "").trim().length > 5;
    return hasName && hasEmail && hasMessage;
  }, [form]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || isSubmitting) return;

    setSubmitError("");
    setSubmitted(false);
    setIsSubmitting(true);

    const endpoint =
      process.env.REACT_APP_CONTACT_ENDPOINT ||
      (process.env.REACT_APP_API_BASE_URL
        ? `${process.env.REACT_APP_API_BASE_URL}/api/public/contact-us`
        : typeof window !== "undefined" && window.location.hostname === "localhost"
          ? "http://localhost:5001/api/public/contact-us"
          : "/api/public/contact-us");

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          company: form.company,
          role: form.role,
          message: form.message,
          website: "",
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || "Failed to send message. Please try again.");
      }

      setSubmitted(true);
      setForm({
        firstName: "",
        lastName: "",
        email: "",
        company: "",
        role: "",
        message: "",
      });
    } catch (err) {
      setSubmitError(err?.message || "Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      component="main"
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
        "@keyframes ibFloatPulse": {
          "0%, 100%": { transform: "translate(-50%, -50%) scale(1)", opacity: 0.55 },
          "50%": { transform: "translate(-50%, -52%) scale(1.06)", opacity: 0.8 },
        },
        "@keyframes ibDriftOrb": {
          "0%, 100%": { transform: "translate(0, 0)" },
          "50%": { transform: "translate(18px, -24px)" },
        },
      }}
    >
      <PageMeta
        title="Contact — InfraBlaze, Control Plane for Agent Driven Infrastructure"
        description="Talk to the InfraBlaze team — request a demo, ask about self-hosted deployment, or get in touch with security or sales."
      />
      {/* Top section: two-column layout */}
      <Box
        sx={{
          position: "relative",
          pt: {
            xs: "calc(56px + env(safe-area-inset-top) + 56px)",
            md: "calc(56px + env(safe-area-inset-top) + 80px)",
          },
          pb: { xs: 8, md: 12 },
        }}
      >
        {/* Floating glow orbs */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: "35%",
            left: "10%",
            width: { xs: 400, md: 700 },
            height: { xs: 400, md: 700 },
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, isDark ? 0.22 : 0.12)}, transparent 60%)`,
            filter: "blur(80px)",
            pointerEvents: "none",
            zIndex: 0,
            animation: reducedMotion ? "none" : "ibFloatPulse 12s ease-in-out infinite",
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: "5%",
            right: "-5%",
            width: { xs: 300, md: 500 },
            height: { xs: 300, md: 500 },
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, isDark ? 0.18 : 0.10)}, transparent 65%)`,
            filter: "blur(70px)",
            pointerEvents: "none",
            zIndex: 0,
            animation: reducedMotion ? "none" : "ibDriftOrb 15s ease-in-out infinite",
          }}
        />

        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 }, position: "relative", zIndex: 1 }}>
          <Grid container spacing={{ xs: 5, md: 7 }} alignItems="flex-start">
            {/* Left: Intro + contact methods */}
            <Grid item xs={12} md={5}>
              <Stack spacing={3} sx={{ position: { md: "sticky" }, top: { md: 120 } }}>
                <Typography
                  variant="h1"
                  sx={{
                    ...fadeUp(0),
                    fontWeight: 950,
                    letterSpacing: -1.0,
                    lineHeight: 1.04,
                    fontSize: { xs: "2.1rem", sm: "2.6rem", md: "3.05rem" },
                  }}
                >
                  Let's build your cloud platform.
                </Typography>

                <Typography
                  variant="h6"
                  color="text.primary"
                  sx={{ ...fadeUp(260), lineHeight: 1.7, maxWidth: 460, fontWeight: 700, fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.4rem" } }}
                >
                  Tell us what you're trying to automate. We'll map InfraBlaze to your
                  cloud footprint and recommend a rollout path.
                </Typography>

                {/* Contact method cards */}
                <Stack spacing={1.5} sx={{ pt: 2 }}>
                  {CONTACT_METHODS.map((method, idx) => {
                    const Icon = method.icon;
                    return (
                      <Box
                        key={method.label}
                        component={Link}
                        href={method.href}
                        underline="none"
                        sx={{
                          ...fadeUp(400 + idx * 100),
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          p: 2,
                          borderRadius: "14px",
                          border: "1px solid",
                          borderColor: alpha(theme.palette.divider, 0.85),
                          backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.45 : 0.92),
                          backdropFilter: "blur(10px)",
                          transition: "transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease",
                          textDecoration: "none !important",
                          "&:hover": {
                            transform: "translateX(4px)",
                            borderColor: alpha(theme.palette.primary.main, 0.5),
                            boxShadow: `0 10px 30px ${alpha(theme.palette.primary.main, isDark ? 0.18 : 0.1)}`,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 44,
                            height: 44,
                            flex: "0 0 auto",
                            borderRadius: "12px",
                            backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.2 : 0.12),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                          }}
                        >
                          <Icon sx={{ color: "primary.main", fontSize: 22 }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 800, color: "text.primary", lineHeight: 1.3 }}
                          >
                            {method.label}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", lineHeight: 1.5, mt: 0.25 }}
                          >
                            {method.description}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              color: "primary.main",
                              fontWeight: 700,
                              mt: 0.5,
                            }}
                          >
                            {method.value}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Stack>
            </Grid>

            {/* Right: Form */}
            <Grid item xs={12} md={7}>
              <Card
                elevation={0}
                sx={{
                  ...fadeUp(300),
                  borderRadius: "20px",
                  border: "1px solid",
                  borderColor: alpha(theme.palette.primary.main, isDark ? 0.35 : 0.25),
                  backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.62 : 0.98),
                  backdropFilter: "blur(14px)",
                  overflow: "hidden",
                  position: "relative",
                  boxShadow: `0 30px 90px ${alpha(theme.palette.primary.main, isDark ? 0.18 : 0.12)}`,
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    background: `radial-gradient(500px 200px at 100% 0%, ${alpha(
                      theme.palette.primary.main,
                      isDark ? 0.2 : 0.12
                    )}, transparent 60%)`,
                  }}
                />

                <CardContent sx={{ p: { xs: 3, md: 4.5 }, position: "relative" }}>
                  <Typography variant="h5" sx={{ fontWeight: 900, mb: 0.5 }}>
                    Send us a message
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
                    We typically respond within 1 business day.
                  </Typography>

                  {submitted ? (
                    <Alert
                      severity="success"
                      icon={<CheckCircleOutlineIcon />}
                      sx={{
                        mb: 3,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.success.main, isDark ? 0.15 : 0.08),
                        border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                      }}
                    >
                      Thanks — we received your message. We'll get back to you soon.
                    </Alert>
                  ) : null}
                  {submitError ? (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                      {submitError}
                    </Alert>
                  ) : null}

                  <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={2.2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="First name"
                          value={form.firstName}
                          onChange={handleChange("firstName")}
                          fullWidth
                          required
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Last name"
                          value={form.lastName}
                          onChange={handleChange("lastName")}
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Work email"
                          value={form.email}
                          onChange={handleChange("email")}
                          fullWidth
                          required
                          type="email"
                          variant="outlined"
                          helperText="Please use your company email."
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Company"
                          value={form.company}
                          onChange={handleChange("company")}
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Role"
                          value={form.role}
                          onChange={handleChange("role")}
                          fullWidth
                          variant="outlined"
                          placeholder="e.g. Platform Engineer"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="How can we help?"
                          value={form.message}
                          onChange={handleChange("message")}
                          fullWidth
                          required
                          multiline
                          minRows={4}
                          variant="outlined"
                          placeholder="Tell us about your cloud footprint, current tooling, and what you'd like to automate."
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          disabled={!isValid || isSubmitting}
                          fullWidth
                          sx={{
                            fontWeight: 900,
                            borderRadius: 999,
                            py: 1.4,
                            fontSize: "0.95rem",
                            textTransform: "none",
                            color: "common.white !important",
                            boxShadow: `0 10px 30px ${alpha(theme.palette.primary.main, 0.35)}`,
                            transition: "transform 0.25s ease, box-shadow 0.25s ease",
                            "&:hover": {
                              color: "common.white !important",
                              transform: "translateY(-2px)",
                              boxShadow: `0 14px 40px ${alpha(theme.palette.primary.main, 0.5)}`,
                            },
                          }}
                        >
                          {isSubmitting ? "Sending..." : "Send message"}
                        </Button>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 1.5, lineHeight: 1.6, textAlign: "center" }}
                        >
                          By submitting, you agree to be contacted by InfraBlaze about product
                          updates and onboarding.
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>

              {/* Trust signals under form */}
              <Box
                sx={{
                  ...fadeUp(550),
                  mt: 3,
                  p: 2.5,
                  borderRadius: "14px",
                  border: "1px dashed",
                  borderColor: alpha(theme.palette.divider, 0.7),
                  backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.35 : 0.85),
                }}
              >
                <Grid container spacing={1.25}>
                  {TRUST_SIGNALS.map((item) => (
                    <Grid item xs={12} sm={6} key={item}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CheckCircleOutlineIcon
                          sx={{ fontSize: 18, color: "primary.main", flex: "0 0 auto" }}
                        />
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                          {item}
                        </Typography>
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ mt: "auto" }}>
        <MarketingInfoBar />
      </Box>
    </Box>
  );
}
